import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import useCommonStore, { setModalConfig } from '@/stores/common';
import {
  Lesson,
  DailyRosterInstructor
} from '@/constants/Interfaces/CommonInterfaces';
import LessonDetailsCard from '@/components/DailyRoster/LessonDetailsPopup/LessonDetailsCard';

interface HoverableLessonCellProps {
  lesson: Lesson;
  instructor: DailyRosterInstructor;
  content: React.ReactNode;
  className?: string;
  hasConflict?: boolean;
  onClick?: () => void;
}

const HoverableLessonCell: React.FC<HoverableLessonCellProps> = ({
  lesson,
  instructor,
  content,
  className = '',
  hasConflict = false,
  onClick
}) => {
  const [coords, setCoords] = useState<{
    x: number;
    y: number;
    side: 'top' | 'bottom';
    arrowOffset: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isGlobalModalOpen = useCommonStore(state => state.modalConfig.visible);

  const calculatePosition = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const viewportWidth = document.documentElement.clientWidth;
    // const viewportHeight = window.innerHeight;

    // 1. Determine size.
    // If the tooltip is already visible, we measure its real width.
    // Otherwise, we use a safe estimate for the first render.
    const tooltipWidth = tooltipRef.current?.offsetWidth || 650;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 400;
    const padding = 20;

    // 2. Vertical Logic
    const side = rect.top < tooltipHeight ? 'bottom' : 'top';

    // 3. Horizontal Logic (Sidebar/Viewport Clamping)
    const idealX = rect.left + rect.width / 2;
    const minX = tooltipWidth / 2 + padding;
    const maxX = viewportWidth - tooltipWidth / 2 - padding;

    // Clamp the X position so it never goes off-screen or behind sidebar
    const clampedX = Math.max(minX, Math.min(maxX, idealX));

    setCoords({
      x: clampedX,
      y: side === 'top' ? rect.top : rect.bottom,
      side,
      arrowOffset: idealX - clampedX
    });
  }, []);

  // Recalculate if the tooltip content changes or on initial hover
  useLayoutEffect(() => {
    if (coords) {
      calculatePosition();
    }
  }, [calculatePosition, !!coords]);

  const handleMouseEnter = () => {
    if (isGlobalModalOpen) return;
    calculatePosition();
  };

  const handleCellClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCoords(null);
      setModalConfig({
        visible: true,
        type: 'updateTransportModal',
        props: { size: '2xl' },
        data: { lesson: { ...lesson }, instructor: { ...instructor } }
      });
      if (onClick) onClick();
    },
    [lesson, instructor, onClick]
  );

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full flex items-center justify-center cursor-pointer transition-all duration-200 hover:brightness-95 active:brightness-90 ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setCoords(null)}
      onClick={handleCellClick}
    >
      <div
        className={
          hasConflict
            ? 'w-full h-full flex flex-col justify-center'
            : 'truncate w-full text-center'
        }
      >
        {content}
      </div>

      {coords &&
        !isGlobalModalOpen &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: 'fixed',
              left: `${coords.x}px`,
              top:
                coords.side === 'top'
                  ? `${coords.y - 12}px`
                  : `${coords.y + 12}px`,
              transform:
                coords.side === 'top'
                  ? 'translate(-50%, -100%)'
                  : 'translate(-50%, 0)',
              zIndex: 9999,
              pointerEvents: 'none',
              width: 'max-content', // Retains original sizing of the Card
              maxWidth: '95vw' // Prevents overflow on very small screens
            }}
            className='animate-in fade-in zoom-in-95 duration-150'
          >
            <div className='bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden'>
              <LessonDetailsCard lesson={lesson} instructor={instructor} />
            </div>

            {/* Dynamic Arrow: Moves to point at the cell even if the card shifts */}
            <div
              className='w-3 h-3 bg-white border-slate-200 rotate-45 absolute left-1/2'
              style={{
                marginLeft: `${coords.arrowOffset}px`,
                transform: 'translateX(-50%) rotate(45deg)',
                bottom: coords.side === 'top' ? '-6px' : 'auto',
                top: coords.side === 'bottom' ? '-6px' : 'auto',
                borderRight:
                  coords.side === 'top' ? '1px solid #e2e8f0' : 'none',
                borderBottom:
                  coords.side === 'top' ? '1px solid #e2e8f0' : 'none',
                borderLeft:
                  coords.side === 'bottom' ? '1px solid #e2e8f0' : 'none',
                borderTop:
                  coords.side === 'bottom' ? '1px solid #e2e8f0' : 'none'
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default HoverableLessonCell;
